import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { 
    Package, 
    AlertCircle, 
    Truck, 
    Shield, 
    Banknote, 
    Star, 
    Clock,
    Gift,
    CheckCircle2,
    ArrowRight,
    Flame,
    Users,
    BadgeCheck,
    Sparkles,
    Phone,
    MapPin,
    Timer
} from 'lucide-react';
import { ProductCard, ProductCardSkeleton } from '../components/product/ProductCard';
import { Pagination } from '../components/ui/Pagination';
import { productsApi } from '../lib/api';
import { scrollToTop } from '../lib/utils';
import { CategoryRail } from '../components/home/CategoryRail';
import { useEffect, useMemo } from 'react';

const PRODUCTS_PER_PAGE = 20;

// Trust badges data
const TRUST_BADGES = [
    { icon: Truck, title: 'Livraison Rapide', desc: 'Partout en Algérie', color: 'blue' },
    { icon: Banknote, title: 'Paiement à la Livraison', desc: 'Payez en espèces', color: 'green' },
    { icon: Shield, title: 'Produits Garantis', desc: 'Qualité assurée', color: 'purple' },
    { icon: Phone, title: 'Support Client', desc: '7j/7 disponible', color: 'orange' },
];

// Testimonials data
const TESTIMONIALS = [
    { name: 'Amina B.', wilaya: 'Alger', text: 'Livraison très rapide et produit conforme. Je recommande!', rating: 5 },
    { name: 'Karim M.', wilaya: 'Oran', text: 'Excellent service, le colis est arrivé bien emballé.', rating: 5 },
    { name: 'Fatima Z.', wilaya: 'Constantine', text: 'Très satisfaite de ma commande, je reviendrai!', rating: 5 },
];

export function HomePage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const currentCategory = searchParams.get('category');

    // Random stats for social proof
    const stats = useMemo(() => ({
        orders: Math.floor(Math.random() * 200) + 350,
        viewers: Math.floor(Math.random() * 15) + 8,
        rating: 4.8,
    }), []);

    // Fetch products using React Query
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['products', currentPage, currentCategory],
        queryFn: () => productsApi.getAll({
            page: currentPage,
            limit: PRODUCTS_PER_PAGE,
            category: currentCategory || undefined,
        }),
        staleTime: 1000 * 60 * 5,
    });

    const products = data?.data || [];
    const totalPages = data?.meta?.totalPages || 1;

    const handlePageChange = (page: number) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', page.toString());
        setSearchParams(newParams);
        scrollToTop();
    };

    useEffect(() => {
        scrollToTop();
    }, [currentCategory]);

    return (
        <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pb-20 md:pb-0">
            {/* Announcement Bar */}
            <div className="bg-gradient-to-r from-primary-600 via-primary-500 to-orange-500 text-white py-2 md:py-2.5 px-3 md:px-4">
                <div className="container-custom flex items-center justify-center gap-2 md:gap-3 text-xs md:text-sm font-medium">
                    <Flame className="w-3 h-3 md:w-4 md:h-4 animate-pulse flex-shrink-0" />
                    <span className="truncate">🔥 <strong>Offre Spéciale</strong> - Livraison GRATUITE +5000 DA!</span>
                    <Flame className="w-3 h-3 md:w-4 md:h-4 animate-pulse flex-shrink-0 hidden sm:block" />
                </div>
            </div>

            {/* Hero Section - Only on home */}
            {!currentCategory && (
                <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8 md:py-12 lg:py-20">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
                    </div>
                    
                    <div className="container-custom relative z-10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                            {/* Left Content */}
                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                {/* Badge */}
                                <div className="inline-flex items-center gap-2 bg-primary-500/20 backdrop-blur-sm border border-primary-400/30 rounded-full px-4 py-2 mb-6">
                                    <Sparkles className="w-4 h-4 text-primary-400" />
                                    <span className="text-sm font-semibold text-primary-300">Nouvelle Collection 2025</span>
                                </div>

                                <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-black text-white leading-tight mb-4 md:mb-6">
                                    Achetez en Toute
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-orange-400"> Confiance</span>
                                </h1>

                                <p className="text-base md:text-lg text-gray-300 mb-6 md:mb-8 max-w-lg leading-relaxed">
                                    Découvrez notre sélection de produits premium. 
                                    <strong className="text-white"> Paiement à la livraison</strong> partout en Algérie.
                                </p>

                                {/* CTA Buttons */}
                                <div className="flex flex-wrap gap-3 md:gap-4 mb-6 md:mb-8">
                                    <Link 
                                        to="#products" 
                                        onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
                                        className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500 to-orange-500 text-white px-5 md:px-8 py-3 md:py-4 rounded-xl font-bold text-base md:text-lg shadow-lg shadow-primary-500/30 hover:shadow-xl active:scale-[0.98] md:hover:scale-105 transition-all touch-manipulation"
                                    >
                                        <Gift className="w-4 h-4 md:w-5 md:h-5" />
                                        <span className="hidden sm:inline">Découvrir les Produits</span>
                                        <span className="sm:hidden">Voir les Produits</span>
                                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                                    </Link>
                                </div>

                                {/* Live Stats */}
                                <div className="flex flex-wrap items-center gap-4 md:gap-6">
                                    <div className="flex items-center gap-2 text-gray-400">
                                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                        <Users className="w-3 h-3 md:w-4 md:h-4" />
                                        <span className="text-xs md:text-sm">{stats.viewers} en ligne</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-400">
                                        <Timer className="w-3 h-3 md:w-4 md:h-4" />
                                        <span className="text-xs md:text-sm">{stats.orders}+ commandes</span>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Right - Trust Cards */}
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="hidden lg:grid grid-cols-2 gap-4"
                            >
                                {TRUST_BADGES.map((badge, i) => (
                                    <motion.div
                                        key={badge.title}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3 + i * 0.1 }}
                                        className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/15 transition-all"
                                    >
                                        <badge.icon className={`w-10 h-10 text-${badge.color}-400 mb-3`} />
                                        <h3 className="font-bold text-white mb-1">{badge.title}</h3>
                                        <p className="text-sm text-gray-400">{badge.desc}</p>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </div>

                    {/* Bottom Wave */}
                    <div className="absolute bottom-0 left-0 right-0">
                        <svg viewBox="0 0 1440 100" fill="none" className="w-full h-auto">
                            <path d="M0 100V60C240 20 480 0 720 0C960 0 1200 20 1440 60V100H0Z" fill="white" />
                        </svg>
                    </div>
                </section>
            )}

            {/* Trust Badges - Mobile */}
            {!currentCategory && (
                <section className="lg:hidden py-6 px-4 -mt-4">
                    <div className="grid grid-cols-2 gap-3">
                        {TRUST_BADGES.slice(0, 4).map((badge) => (
                            <div key={badge.title} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
                                <badge.icon className={`w-8 h-8 mx-auto mb-2 text-${badge.color}-500`} />
                                <p className="text-xs font-semibold text-gray-800">{badge.title}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Categories Rail */}
            <CategoryRail />

            {/* Products Section */}
            <section className="py-8 md:py-12 bg-white" id="products">
                <div className="container-custom">
                    {/* Section Header */}
                    <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                {currentCategory ? (
                                    <span className="text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                                        {currentCategory}
                                    </span>
                                ) : (
                                    <span className="text-sm font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full flex items-center gap-1">
                                        <Flame className="w-3 h-3" />
                                        Produits Populaires
                                    </span>
                                )}
                            </div>
                            <h2 className="font-display font-bold text-2xl md:text-3xl text-secondary-900">
                                {currentCategory ? currentCategory : 'Nos Meilleures Ventes'}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                {!isLoading && data?.meta && (
                                    <span className="flex items-center gap-2">
                                        <BadgeCheck className="w-4 h-4 text-green-500" />
                                        {data.meta.total} produits disponibles
                                    </span>
                                )}
                            </p>
                        </div>

                        {/* Rating Badge */}
                        <div className="flex items-center gap-3 bg-amber-50 px-4 py-2 rounded-xl border border-amber-100">
                            <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                                ))}
                            </div>
                            <span className="text-sm font-semibold text-amber-800">{stats.rating}/5</span>
                            <span className="text-xs text-amber-600">({stats.orders}+ avis)</span>
                        </div>
                    </div>

                    {/* Error State */}
                    {isError && (
                        <div className="text-center py-12 bg-red-50 rounded-2xl border border-red-100">
                            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                            <p className="text-red-600 font-medium mb-4">
                                {error instanceof Error ? error.message : 'Erreur de chargement'}
                            </p>
                            <button onClick={() => refetch()} className="btn-primary">
                                Réessayer
                            </button>
                        </div>
                    )}

                    {/* Loading State */}
                    {isLoading && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <ProductCardSkeleton key={i} />
                            ))}
                        </div>
                    )}

                    {/* Products Grid */}
                    {!isLoading && !isError && products.length > 0 && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                                {products.map((product: any, index: number) => (
                                    <ProductCard key={product.id} product={product} index={index} />
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <div className="mt-12 flex justify-center">
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* Empty State */}
                    {!isLoading && !isError && products.length === 0 && (
                        <div className="text-center py-16 bg-gray-50 rounded-2xl">
                            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="font-bold text-xl text-gray-900 mb-2">Aucun produit</h3>
                            <p className="text-gray-500 mb-6">Essayez une autre catégorie</p>
                            {currentCategory && (
                                <button
                                    onClick={() => {
                                        const newParams = new URLSearchParams(searchParams);
                                        newParams.delete('category');
                                        setSearchParams(newParams);
                                    }}
                                    className="btn-primary"
                                >
                                    Voir tous les produits
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* Testimonials Section */}
            {!currentCategory && (
                <section className="py-12 md:py-16 bg-gradient-to-b from-gray-50 to-white">
                    <div className="container-custom">
                        <div className="text-center mb-10">
                            <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 bg-primary-50 px-4 py-2 rounded-full mb-4">
                                <Star className="w-4 h-4" />
                                Avis Clients
                            </span>
                            <h2 className="font-display font-bold text-2xl md:text-3xl text-secondary-900">
                                Nos Clients Nous Font Confiance
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {TESTIMONIALS.map((t, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    viewport={{ once: true }}
                                    className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
                                >
                                    <div className="flex items-center gap-1 mb-4">
                                        {[...Array(t.rating)].map((_, j) => (
                                            <Star key={j} className="w-5 h-5 text-amber-400 fill-amber-400" />
                                        ))}
                                    </div>
                                    <p className="text-gray-700 mb-4 leading-relaxed">"{t.text}"</p>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                                            {t.name.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900">{t.name}</p>
                                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {t.wilaya}
                                            </p>
                                        </div>
                                        <BadgeCheck className="w-5 h-5 text-green-500 ml-auto" />
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* COD Trust Section */}
            {!currentCategory && (
                <section className="py-12 md:py-16 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
                    <div className="container-custom">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                            <div>
                                <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-semibold mb-6">
                                    <Banknote className="w-4 h-4" />
                                    Paiement Sécurisé
                                </span>
                                <h2 className="font-display font-bold text-3xl md:text-4xl mb-4">
                                    💵 Paiement à la Livraison
                                </h2>
                                <p className="text-lg text-green-100 mb-6 leading-relaxed">
                                    Pas de carte bancaire ? Pas de problème ! Payez en espèces directement au livreur 
                                    quand vous recevez votre commande.
                                </p>
                                <div className="space-y-3">
                                    {[
                                        'Livraison dans les 58 wilayas',
                                        'Vérifiez le produit avant de payer',
                                        'Retour gratuit si non conforme'
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <CheckCircle2 className="w-5 h-5 text-green-300" />
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="hidden lg:flex justify-center">
                                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                                    <div className="text-center">
                                        <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Truck className="w-12 h-12" />
                                        </div>
                                        <p className="text-2xl font-bold mb-2">Livraison Express</p>
                                        <p className="text-green-200">2-5 jours ouvrés</p>
                                        <div className="mt-6 flex items-center justify-center gap-2">
                                            <Clock className="w-5 h-5" />
                                            <span className="text-sm">Suivi en temps réel</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
